import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useIxcProspectusV1ProspectusTaskQueryServiceProspectusTaskGet } from '@interxfi/api-client/generated/hooks';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list/readonly/item/readonly-list-item';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcProspectusV1ProspectusTaskState } from '@interxfi/types';
import { ProspectusTaskEventDrawer, ProspectusTaskEventSummary } from '../../../../../../../../../../features/prospectus/task/prospectus-task-event';
import { getProspectusTaskStateListItems } from '../../../../../../../../../../features/prospectus/task/prospectus-task-list-items';
import { getProspectusTaskDetailPath } from '../../../../../../../../../../features/prospectus/task/prospectus-task-routing';
import { ProspectusTaskStatusPill } from '../../../../../../../../../../features/prospectus/task/prospectus-task-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/beta-nxt/prospectus-task/_task/$taskId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `prospectus-task-${params.taskId}`,
        label: 'Task',
        to: getProspectusTaskDetailPath(params.taskId),
        suffix: <CopyButton content={params.taskId} label="Copy Task ID" tooltip={{ copy: params.taskId, copied: 'Copied Task ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Prospectus Task'),
  component: ProspectusTaskDetail,
});

function getListItems(data: IxcProspectusV1ProspectusTaskState | undefined) {
  const items = getProspectusTaskStateListItems(data);

  return {
    left: [items.data.spec.securityName, items.data.spec.cusip, items.data.spec.isin] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.taskId, items.jobId, items.data.spec.tradeDate] as const satisfies ReadonlyListItem[],
    result: items?.data?.result
      ? ([
          items.data.result.totalShares,
          items.data.result.tradeCount,
          items.data.result.mediantStatus,
          items.data.result.committedAt,
          items.data.result.lastPolledAt,
          items.data.result.segmentToken,
        ] as const satisfies ReadonlyListItem[])
      : undefined,
  } as const;
}

function getEntityMenu(taskId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: ProspectusTaskEventDrawer,
        props: { request: { taskId } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load prospectus task', 'view this prospectus task');

function ProspectusTaskDetail() {
  const { taskId } = Route.useParams();
  const request = { taskId } as const;
  const { data, error, isPending } = useIxcProspectusV1ProspectusTaskQueryServiceProspectusTaskGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const menuItems = useMemo(() => getEntityMenu(taskId), [taskId]);

  const listItems = useMemo(() => getListItems(data?.prospectusTask), [data?.prospectusTask]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <ProspectusTaskStatusPill isPending={showPending} size="large" status={data?.prospectusTask?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <ProspectusTaskEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      {listItems.result ? (
        <PageLayout.Section>
          <VerticalStack gap={4}>
            <Typography.Level3>Result</Typography.Level3>
          </VerticalStack>

          <AppReadonlyList isPending={showPending} items={listItems.result} />
        </PageLayout.Section>
      ) : null}
    </PageLayout>
  );
}
